import { AppLink } from '../../lib/app-navigation';
import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import { Books, Broadcast, Calculator, CalendarBlank, CaretUp, Chats, FileText, FolderSimple, Gear, Handshake, SquaresFour, UserCircle, Users, Storefront } from '@phosphor-icons/react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import type { Language } from '../../lib/crs';
import { useAuth, rememberAuthReturn } from '../../lib/auth';
import { authFailureMessage, safeReturnPath } from '../../lib/auth-safety';
import { navigateInApp } from '../../lib/app-navigation';
import { Brand } from '../shared/SiteChrome';
import { Skeleton } from '../shared/Skeleton';
import './ToolSidebar.css';

export const TOOL_NAVIGATION = [
  { id: 'crs', href: '/assessment', paths: ['/assessment', '/crs-calculator', '/tools'], icon: Calculator, zh: 'CRS计算器', en: 'CRS calculator' },
  { id: 'assessment', href: '/documents', paths: ['/assessment/results', '/pathway-finder', '/documents', '/reports'], icon: FileText, zh: '综合测评', en: 'Comprehensive assessment' },
  { id: 'policy', href: '/reminders', paths: ['/reminders'], icon: Broadcast, zh: '政策雷达', en: 'Policy radar' },
  { id: 'draws', href: '/draws', paths: ['/draws', '/express-entry-draw-tracker'], icon: CalendarBlank, zh: '历史抽签', en: 'Draw history' },
] as const;

export type ToolId = typeof TOOL_NAVIGATION[number]['id'];

export function toolForPath(pathname: string): ToolId | null {
  const path = pathname.replace(/\/$/, '') || '/';
  return TOOL_NAVIGATION.find(tool => (tool.paths as readonly string[]).includes(path))?.id ?? null;
}

function WorkspaceAccount({ language, onClose }: {
  language: Language; onClose: () => void;
}) {
  const auth = useAuth();
  const tr = (zh: string, en: string) => language === 'zh' ? zh : en;
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const root = useRef<HTMLDivElement>(null), trigger = useRef<HTMLButtonElement>(null);
  const pending = useRef(false), navigationPending = useRef(false), mounted = useRef(true);
  const generation = useRef(0), owner = auth.user?.id ?? null, currentOwner = useRef(owner);
  const retryFocus = useRef<{owner: string | null; generation: number; target: HTMLButtonElement; restore: boolean} | null>(null);
  currentOwner.current = owner;

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    generation.current += 1; pending.current = false; navigationPending.current = false;
    retryFocus.current = null; setBusy(false); setError('');
  }, [owner]);
  useLayoutEffect(() => {
    const focus = retryFocus.current;
    if (!focus?.restore || busy || auth.loading) return;
    retryFocus.current = null;
    if (owner !== focus.owner || focus.generation !== generation.current || focus.target !== trigger.current) return;
    const active = document.activeElement;
    if (active === document.body || active === document.documentElement || root.current?.contains(active)) focus.target.focus();
  }, [owner, busy, auth.loading]);

  const enterAuth = () => {
    if (auth.loading || busy || auth.logoutPending || navigationPending.current) return;
    navigationPending.current = true;
    const next = safeReturnPath(`${window.location.pathname}${window.location.search}${window.location.hash}`, window.location.origin);
    rememberAuthReturn(next); onClose();
    navigateInApp(`/login?next=${encodeURIComponent(next)}`);
  };
  const retrySignOut = async () => {
    if (pending.current) return;
    const operation = ++generation.current, operationOwner = currentOwner.current;
    const isCurrent = () => mounted.current && generation.current === operation && currentOwner.current === operationOwner;
    retryFocus.current = trigger.current && root.current?.contains(document.activeElement)
      ? {owner: operationOwner, generation: operation, target: trigger.current, restore: false} : null;
    pending.current = true; setBusy(true); setError('');
    try {
      await auth.retrySignOut();
      if (isCurrent() && retryFocus.current) retryFocus.current = {...retryFocus.current, restore: true};
    }
    catch (failure) { if (isCurrent()) { retryFocus.current = null; setError(authFailureMessage(failure, language)); } }
    finally { if (isCurrent()) { pending.current = false; setBusy(false); } }
  };
  const identifier = auth.user?.email || auth.user?.phone || '';
  const page = new URL(`${window.location.pathname}${window.location.search}${window.location.hash}`, window.location.origin);
  const returnTo = page.pathname === '/account' ? page.searchParams.get('returnTo') : `${page.pathname}${page.search}${page.hash}`;
  const safeReturn = returnTo ? safeReturnPath(returnTo, window.location.origin) : null;
  const settingsHref = safeReturn && !/^\/account(?:[?#]|$)/.test(safeReturn) ? `/account?returnTo=${encodeURIComponent(safeReturn)}` : '/account';


  return <div ref={root} className="halo-workspace-account">
    {error && <p className="halo-workspace-account-error" role="alert">{error}</p>}
    {auth.user ? <Menu key={owner}>
    <MenuButton id="halo-account-toggle" type="button" className="halo-workspace-account-trigger"
      aria-label={tr(`账号菜单：${identifier}`, `Account menu: ${identifier}`)}>
      <UserCircle size={28} weight="regular" aria-hidden="true" />
      <span className="halo-workspace-account-identity" title={identifier}><strong>{identifier}</strong></span>
      <CaretUp size={14} aria-hidden="true" />
    </MenuButton>
    <MenuItems id="halo-account-menu" portal modal={false} anchor="top start" className="halo-workspace-account-menu">
      <MenuItem>{({ close }) => <AppLink to={`${settingsHref}#halo-account-identity`} onClick={() => { close(); onClose(); }} className="halo-workspace-account-menu-item"><UserCircle size={18} aria-hidden="true"/>{tr('账号', 'Account')}</AppLink>}</MenuItem>
      <MenuItem>{({ close }) => <AppLink to={settingsHref} onClick={() => { close(); onClose(); }} className="halo-workspace-account-menu-item"><Gear size={18} aria-hidden="true"/>{tr('设置', 'Settings')}</AppLink>}</MenuItem>
    </MenuItems>
    </Menu> : <button ref={trigger} id="halo-account-toggle" type="button" className="halo-workspace-account-trigger is-anonymous"
      aria-label={auth.loading ? tr('正在检查账户…', 'Checking account…') : tr('未登录', 'Sign in')}
      aria-busy={auth.loading || busy} disabled={auth.loading || busy || auth.logoutPending} onClick={enterAuth}>
      <UserCircle size={28} weight="regular" aria-hidden="true" />
      <span className="halo-workspace-account-identity"><strong>{auth.loading ? <Skeleton width={116} height={16}/> : tr('未登录', 'Sign in')}</strong></span>
    </button>}
    {!auth.user && auth.logoutPending && <button type="button" className="halo-workspace-account-retry" aria-label={busy ? tr('重试完成退出', 'Retry sign-out') : undefined} aria-busy={busy} disabled={busy || auth.loading} onClick={() => void retrySignOut()}>{busy ? <Skeleton width={80} height={14}/> : tr('重试完成退出', 'Retry sign-out')}</button>}
  </div>;
}

export function ToolSidebar({ language, active, open, onClose, currentPath='', role='applicant', advisorAccess=false }: {
  language: Language; active: ToolId | null; open: boolean; onClose: () => void; assessmentHref?: string; currentPath?: string;
  role?: 'applicant' | 'advisor'; advisorAccess?: boolean;
}) {
  const sidebarRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!open) return;
    const toggle = () => document.getElementById('halo-tools-toggle');
    const frame = requestAnimationFrame(() => {
      const nav = sidebarRef.current?.querySelector('nav');
      (nav?.querySelector<HTMLAnchorElement>('[aria-current="page"]') ?? nav?.querySelector<HTMLAnchorElement>('a'))?.focus();
    });
    const outside = (event: PointerEvent) => {
      if (!sidebarRef.current?.contains(event.target as Node) && !toggle()?.contains(event.target as Node) && !document.getElementById('halo-account-menu')?.contains(event.target as Node)) onClose();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || document.getElementById('halo-account-menu')?.contains(event.target as Node)) return;
      event.preventDefault(); onClose(); toggle()?.focus();
    };
    const desktop = window.matchMedia('(min-width:1001px)');
    const resize = () => { if (desktop.matches) onClose(); };
    document.addEventListener('pointerdown', outside); document.addEventListener('keydown', escape); desktop.addEventListener('change', resize);
    return () => { cancelAnimationFrame(frame); document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); desktop.removeEventListener('change', resize); };
  }, [open, onClose]);

  return <aside id="halo-workspace-navigation" ref={sidebarRef} className={`halo-tool-sidebar${open ? ' is-open' : ''}`} onBlur={event => {
    if (open && event.relatedTarget && !sidebarRef.current?.contains(event.relatedTarget as Node) && !document.getElementById('halo-account-menu')?.contains(event.relatedTarget as Node) && !(event.relatedTarget as Element).closest('#halo-tools-toggle')) onClose();
  }}>
    <div className="halo-tool-sidebar-brand"><Brand language={language} /></div>
    <nav id="halo-tool-navigation" aria-label={language === 'zh' ? 'Halo 导航' : 'Halo navigation'} onKeyDown={event => {
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      const links = Array.from(event.currentTarget.querySelectorAll<HTMLAnchorElement>('a'));
      const index = links.indexOf(document.activeElement as HTMLAnchorElement);
      if (index < 0) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? links.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length;
      links[next]?.focus();
    }}>
      {role === 'advisor' ? <><ul>{[
        { href:'/consultants/workspace', icon:SquaresFour, zh:'工作台', en:'Workspace' },
        { href:'/consultants/clients', icon:Users, zh:'客户', en:'Clients' },
        { href:'/consultants/consultations', icon:Chats, zh:'咨询', en:'Consultations' },
        { href:'/consultants/applications', icon:FileText, zh:'申请', en:'Applications' },
        { href:'/consultants/documents', icon:FolderSimple, zh:'资料', en:'Documents' },
      ].map(item => <li key={item.href}><AppLink to={item.href} aria-current={currentPath === item.href || currentPath.startsWith(`${item.href}/`) ? 'page' : undefined} onClick={onClose}><item.icon size={21} weight="regular" aria-hidden="true"/><span>{language === 'zh' ? item.zh : item.en}</span></AppLink></li>)}</ul><div className="halo-sidebar-secondary">{[
        { href:'/consultants/policies', icon:Broadcast, zh:'政策动态', en:'Policy updates' },
        { href:'/consultants/resources', icon:Books, zh:'资源库', en:'Resources' },
        { href:'/consultants/shop', icon:Storefront, zh:'我的店铺', en:'My shop' },
      ].map(item => <AppLink key={item.href} to={item.href} aria-current={currentPath === item.href ? 'page' : undefined} onClick={onClose}><item.icon size={21} weight="regular" aria-hidden="true"/><span>{language === 'zh' ? item.zh : item.en}</span></AppLink>)}</div></> : <><ul>{TOOL_NAVIGATION.map(tool => {
        const href = tool.href;
        return <li key={tool.id}><AppLink to={href} aria-current={active === tool.id ? 'page' : undefined} onClick={event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          onClose(); if (open) requestAnimationFrame(() => document.getElementById('halo-tools-toggle')?.focus());
        }}><tool.icon size={21} weight="regular" aria-hidden="true" /><span>{language === 'zh' ? tool.zh : tool.en}</span></AppLink></li>;
      })}</ul>
      <div className="halo-sidebar-secondary">{[
        {href:'/resources', icon:Books, zh:'资源库', en:'Resources', selected:currentPath === '/resources' || currentPath === '/programs' || currentPath.startsWith('/resources/') || currentPath.startsWith('/programs/')},
        {href:'/materials', icon:FolderSimple, zh:'材料准备', en:'Document preparation', selected:currentPath === '/materials'},
      ].map(item => <AppLink key={item.href} to={item.href} aria-current={item.selected ? 'page' : undefined} onClick={onClose}><item.icon size={20} weight="regular" aria-hidden="true" /><span>{language === 'zh' ? item.zh : item.en}</span></AppLink>)}</div>
      <div className="halo-sidebar-professional" aria-label={language === 'zh' ? '专业服务' : 'Professional services'}>
        <p>{language === 'zh' ? '专业服务' : 'Professional services'}</p>
        <AppLink to="/workspace/advisors" aria-current={currentPath === '/workspace/advisors' || currentPath.startsWith('/workspace/advisors/') ? 'page' : undefined} onClick={onClose}><Handshake size={20} aria-hidden="true" /><span>{language === 'zh' ? '顾问市场' : 'Find an adviser'}</span></AppLink>
        <AppLink to="/workspace/consultations" aria-current={currentPath === '/workspace/consultations' || currentPath.startsWith('/workspace/consultations/') ? 'page' : undefined} onClick={onClose}><Chats size={20} aria-hidden="true" /><span>{language === 'zh' ? '我的咨询' : 'My consultations'}</span></AppLink>
        <AppLink to="/workspace/applications" aria-current={currentPath === '/workspace/applications' || currentPath.startsWith('/workspace/applications/') ? 'page' : undefined} onClick={onClose}><FolderSimple size={20} aria-hidden="true"/><span>{language === 'zh' ? '我的申请' : 'My applications'}</span></AppLink>
      </div></>}
      {role === 'applicant' && advisorAccess && <div className="halo-sidebar-professional">
        <AppLink to="/consultants/workspace" onClick={onClose}>{language === 'zh' ? '顾问工作台' : 'Advisor workspace'}</AppLink>
      </div>}
    </nav>
    <WorkspaceAccount language={language} onClose={onClose}/>
  </aside>;
}

export function WorkspaceBar({language,open,onToggle,onLanguage}: {
  language:Language;open:boolean;onToggle:()=>void;onLanguage:(language:Language)=>void;
}) {
  return <header className="halo-workspace-bar">
    <div className="halo-workspace-bar-inner">
      <Brand language={language}/>
      <div className="halo-workspace-bar-actions">
        <button type="button" className="halo-workspace-language" aria-label={language === 'zh' ? '切换到英语' : 'Switch to Chinese'} onClick={() => onLanguage(language === 'zh' ? 'en' : 'zh')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/><path d="M3 12h18M12 3c3 3 4 6 4 9s-1 6-4 9c-3-3-4-6-4-9s1-6 4-9Z" stroke="currentColor" strokeWidth="1.5"/></svg>
          <span>{language === 'zh' ? 'EN' : '中文'}</span>
        </button>
        <button id="halo-tools-toggle" className="halo-workspace-menu-toggle" type="button" aria-controls="halo-workspace-navigation" aria-expanded={open}
          aria-label={language === 'zh' ? (open ? '关闭菜单' : '打开菜单') : (open ? 'Close menu' : 'Open menu')} onClick={onToggle}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d={open ? 'M5 5L19 19M19 5L5 19' : 'M3 6H21M3 12H21M3 18H21'} stroke="currentColor" strokeWidth="1.5"/></svg>
        </button>
      </div>
    </div>
  </header>;
}
